import { ReactElement, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { useViewTracking } from '~/utils/use-view-tracking';
import { $CurrentPageModule } from '~/models/current-page-module';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import ChevronRightIcon from '@lemon8/design/lib/icons/icon_assets/chevron_right_fill.svg';
import { ClickPosition } from '~/utils/click-position';

import './style.scss';

interface SectionTitleProps {
  title: string | ReactElement;
  viewMore?: boolean;
  onClickViewMore?: () => void;
  onShowViewMore?: () => void;
  /** 在title前面显示 */
  emojiStr?: string;
}

/**
 * 通用的小标题，右侧有view more按钮
 */
function SectionTitleWithViewMore(props: SectionTitleProps): JSX.Element {
  const { title, onClickViewMore, onShowViewMore, emojiStr = '' } = props;

  const currentPageModule = useScopedAtomValue($CurrentPageModule);
  const prefix = useScopedAtomValue($CurrentPositionPrefix);

  const { t } = useTranslation();

  const onClick = (): void => {
    window.Tea('wap_click', {
      current_page_module: currentPageModule,
      current_position: prefix + ClickPosition.view_more_button,
      is_cta: 1,
    });

    onClickViewMore?.();
  };

  const sectionObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.view_more_button,
      });

      onShowViewMore?.();
    }, []),
  });

  return (
    <div className="section-title-container" id="section-title-view-more" ref={sectionObservedRef}>
      <h2 className="section-title">
        {Boolean(emojiStr) && <span className="emoji">{emojiStr}</span>}
        {title}
      </h2>
      <div className="view-more" onClick={onClick}>
        <span>{t('seo_recommended_viewMore')}</span>
        <ChevronRightIcon />
      </div>
    </div>
  );
}

/**
 * 通用的小标题
 */
export default function SectionTitle(props: SectionTitleProps): JSX.Element {
  const { title, viewMore = false, emojiStr = '' } = props;

  const { isMobile } = useAtomValue($UserAgent);

  // pc端默认不展示view more按钮
  if (!viewMore || !isMobile) {
    return (
      <h2 className="section-title">
        {Boolean(emojiStr) && <span className="emoji">{emojiStr}</span>}
        {title}
      </h2>
    );
  }

  return <SectionTitleWithViewMore {...props} />;
}
